import { types, getEnv, flow, SnapshotOut, Instance } from 'mobx-state-tree';
import { IAppEnv } from '../appEnvironment';
import { ISportsBettingApi } from '../api/api';
import { gameGroupStore } from './gameGroup';
import { gameStore } from './game';
import { gameDetailStore, createOdds } from './gameDetail';
import { getEventDetailParamFromKey } from './mainCategoryMenu';
import { observable } from 'mobx';
import { IGroupedApiCtx, ProgressStateModelForStore, contract, errorIsCancel, parseTipIdent, ProgressStateModel, protoUtils, IOdd, IOddsExtData, rootCategoryId, IRenameTipData, canConvertTipName, getSimpleRenameTipDataProvider, IRenameTipDataProvider, StatisticsInApiStore, StatisticsType, IMatchStatsInfo, OfferType, selectionVisualisationFlags2GameBusinessType, createCategoryKey } from 'eTip--common';
import { EventTvModel } from './eventTv';

export const eventDetailBreadcrumbStore = types.model({
    categoryId: types.string,
    title: types.string
});

export const eventDetailStore = types
    .model({
        sport: types.maybeNull(types.string),
        region: types.maybeNull(types.string),
        competition: types.maybeNull(types.string),
        Date: types.maybeNull(types.Date),
        GameGroups: types.optional(types.array(gameGroupStore), []),
        GraphicsPresentationID: types.maybeNull(types.number),
        eventId: types.optional(types.number, -1),
        eventKeyIfEmpty: types.maybeNull(types.string),
        categoryId: types.optional(types.string, rootCategoryId),
        Name: types.optional(types.string, ''),
        state: types.optional(ProgressStateModelForStore, ProgressStateModel.NotInitialized),
        gameInfo: types.optional(types.string, ''),
        EventTvs: types.maybeNull(types.array(EventTvModel)),
        statistics: types.maybeNull(StatisticsInApiStore),
        Info: types.maybeNull(types.string),
        EventMarketingMessage: types.maybeNull(types.string),
        lastReloadTime: types.maybeNull(types.number),
        isLiveAvailable: types.maybeNull(types.boolean),
        breadcrumbs: types.maybeNull(types.array(eventDetailBreadcrumbStore))
    })
    .views(self => {
        const env = getEnv<IAppEnv>(self);
        return {
            get key() {
                return `${self.eventId}c${self.categoryId}`;
            },
            get league() {
                if (!!self.region) {
                    if (!!self.competition) {
                        return self.region.concat(` - ${self.competition}`);
                    }
                    return self.region;
                }
                return "";
            },
            get sortedGameGroups() {
                return self.GameGroups;
            },
            get refreshTime() {
                return env.settings.eventDetailRefreshTime;
            },
            get showFavoritesTooltip() {
                return env.loggedUserId === 0;
            },
            get statsInfo() {
                if (self.statistics) {
                    return <IMatchStatsInfo>{
                        onStatisticsAction: env.eventStats.viewStatistics,
                        statistics: self.statistics
                    };
                }
                return null;
            },

            getCouponTipData(ident: string): { odd: IOdd, extData: IOddsExtData } {
                const parseIdent = parseTipIdent(ident);
                let g: Instance<typeof gameStore>;
                self.GameGroups.some((gameGroup: Instance<typeof gameGroupStore>) => {
                    g = gameGroup.Games.find((game: Instance<typeof gameStore>) => game.ID === parseIdent.gameID);
                    return !!g;
                });
                if (g) {
                    let d = g.Details.find(d => d.ID === parseIdent.detailID);
                    if (d) {
                        return {
                            odd: d.getOddForTicket(ident),
                            extData: {
                                eventName: self.Name,
                                gameName: g.Name,
                                eventDate: self.Date,
                                hasDetail: g.IsDetails,
                                detailName: d.Name,
                                number: d.LineNumber,
                                offerType: OfferType.Prematch
                            }
                        }
                    }
                }
                return null;
            }
        };
    })
    .actions(self => {
        const env = getEnv<IAppEnv>(self);
        let ctx: IGroupedApiCtx<ISportsBettingApi> | null = null;
        const loadAsync: (params: { eventKey: string }) => Promise<void> = flow(function* (params: { eventKey: string }) {
            self.state = ProgressStateModel.Loading;
            try {
                if (ctx && ctx.isOpen()) {
                    ctx.cancel('Load has been canceled.');
                }
                ctx = env.api.open();
                let eventDetailParam = getEventDetailParamFromKey(params.eventKey);
                const r: contract.GetWebStandardEventExtResponse = yield ctx.api.getWebStandardEventAsync(eventDetailParam);
                if (!!r && !!r.Event) {
                    set(r.Event.EventTree, params.eventKey, r.IsLiveAvailable);
                    self.lastReloadTime = r.Event.TimeStamp;
                }
                self.state = ProgressStateModel.Success;
            } catch (error) {
                if (errorIsCancel(error)) {
                    throw error;
                }
                env.loggingService.error('Error in event detail loading.', error);
                self.state = ProgressStateModel.Failed;
            } finally {
                ctx = null;
            }
        });

        //is it the same as load func?
        const reloadAsync: () => Promise<void> = flow(function* () {
            try {
                if (ctx && ctx.isOpen()) {  //TODO incorrect, depends on full load, it must not cancel full load, see e.g. topBetsBase
                    ctx.cancel('Load has been canceled.');
                }
                ctx = env.api.open();
                let eventId: number;
                let categoryId: string;
                if (self.eventId) {
                    eventId = self.eventId;
                    categoryId = self.categoryId;
                } else {
                    let eventDetailParam = getEventDetailParamFromKey(self.eventKeyIfEmpty);
                    eventId = eventDetailParam.eventId;
                    categoryId = eventDetailParam.categoryId;
                }
                const r: contract.GetWebStandardEventExtResponse = yield ctx.api.getWebStandardEventAsync({ eventId: eventId, categoryId: categoryId, timeStamp: self.lastReloadTime });
                if (!!r && !!r.Event) {
                    set(r.Event.EventTree, self.eventKeyIfEmpty, r.IsLiveAvailable);
                    self.lastReloadTime = r.Event.TimeStamp;
                }
                self.state = ProgressStateModel.Success;
            } catch (error) {
                if (errorIsCancel(error)) {
                    throw error;
                }
                env.loggingService.error('Error in event detail loading.', error);
            } finally {
                ctx = null;
            }
        });

        const setEvent = (event: contract.IEvent, sport: contract.ISecondLevelCategory, region: string = "", competition: string = "") => {
            self.eventId = event.Id;
            self.Name = event.Name;
            self.GraphicsPresentationID = sport.GraphicsPresentationId;
            self.categoryId = event.CategoryId;
            self.sport = sport.Base ? sport.Base.Name : "";
            self.competition = competition;
            self.region = region;

            self.GameGroups = observable(createGameGroups(event.GameGroups, event, getRenameDataProvider(event)));
            self.gameInfo = event.Info;
            if (event.Date) {
                self.Date = protoUtils.protoTimeStamp2Date(event.Date);
            }
            self.EventTvs =
                event.Tvs && event.Tvs.length
                    ? observable(
                        event.Tvs.map(e => {
                            return { id: e.Id, name: e.Name, date: protoUtils.protoTimeStamp2Date(event.Date) }
                        })
                    )
                    : undefined;
            if (event.StatisticsExternalId?.length) {
                self.statistics = StatisticsInApiStore.create(<SnapshotOut<typeof StatisticsInApiStore>>{ id: event.StatisticsExternalId, type: StatisticsType.BRMatch });
            } else {
                self.statistics = null;
            }

            self.EventMarketingMessage = event.MarketingMessage;
        }

        const getRenameDataProvider = (event: contract.IEvent): IRenameTipDataProvider => {
            return getSimpleRenameTipDataProvider(event.Name);
        }

        const set = (detail: contract.IFirstLevelCategory, eventKey: string, isLiveAvailable: boolean) => {
            self.eventKeyIfEmpty = null;
            self.isLiveAvailable = isLiveAvailable;
            let breadcrumbs: { categoryId: string, title: string }[] = [];
            if (detail != null && detail.Categories.length) {
                const sport = detail.Categories[0];

                if (sport.Base) {
                    const data = { categoryId: sport.Base.Id, title: sport.Base.Name };
                    breadcrumbs.push(data);
                }

                if (sport.Categories.length) {
                    const region = sport.Categories[0];
                    if (region.Categories.length) { //!!! same ifs in eTip\eventDetail and eTipMobile\eventDetail
                        const competition = region.Categories[0];
                        if (competition.Base.Events.length) {
                            let breadcrumbsIds = [];
                            if (sport.Base) {
                                breadcrumbsIds.push(sport.Base.Id);
                            }
                            breadcrumbsIds.push(competition.Base.ParentId);
                            breadcrumbsIds.push(competition.Base.Id);
                            const breadcrumb = { categoryId: createCategoryKey(breadcrumbsIds), title: competition.Base.Name };
                            breadcrumbs.push(breadcrumb);
                            setEvent(competition.Base.Events[0], sport, region.Base ? region.Base.Name : "", competition.Base.Name);
                        }
                    } else if (region.Base && region.Base.Events && region.Base.Events.length) {

                        const breadcrumb = { categoryId: createCategoryKey([region.Base.ParentId, region.Base.Id]), title: region.Base.Name };
                        breadcrumbs.push(breadcrumb);
                        setEvent(region.Base.Events[0], sport, region.Base.Name);
                    }
                }
                else if (sport.Base.Events && sport.Base.Events.length) {
                    setEvent(sport.Base.Events[0], sport)
                }

            } else {
                self.eventId = 0;
                self.eventKeyIfEmpty = eventKey;
                self.Name = '';
                self.categoryId = rootCategoryId;
                self.GraphicsPresentationID = 0;
                self.GameGroups = observable([]);
            }
            if (breadcrumbs.length) {
                self.breadcrumbs = observable(breadcrumbs);
            } else {
                self.breadcrumbs = null;
            }
        };

        const createGameGroups = (gameGroups: contract.IGameGroup[], event: contract.IEvent, tipRenameDataProvider: IRenameTipDataProvider) => {
            return gameGroups.map(g =>
                gameGroupStore.create(<SnapshotOut<typeof gameGroupStore>>{
                    ID: g.ID,
                    Name: g.Name,
                    Games: g.Games.map(gg => {

                        let tipRenameData: IRenameTipData = canConvertTipName(gg) ? tipRenameDataProvider.get() : null;
                        return gameStore.create(<SnapshotOut<typeof gameStore>>{
                            ID: gg.ID,
                            Name: gg.Name,
                            BusinessType: gg.BusinessType,
                            Sequence: gg.Sequence,
                            Details: gg.Details.map(ggd =>
                                gameDetailStore.create(<SnapshotOut<typeof gameDetailStore>>{
                                    ID: ggd.ID,
                                    Name: ggd.Name,
                                    LineNumber: ggd.LineNumber,
                                    Odds: createOdds(
                                        ggd.OddsList.map(odd => {
                                            return {
                                                DetailID: ggd.ID,
                                                EventID: event.Id,
                                                GameID: gg.ID,
                                                Name: odd.Name,
                                                PopularityPercentage: 0,
                                                Rate: odd.Rate,
                                                TipID: odd.TipID,
                                                State: odd.State
                                            };
                                        }),
                                        __IsMTS__ ? selectionVisualisationFlags2GameBusinessType(gg.SelectionVisualisationFlags) : gg.BusinessType,
                                        ggd.Name,
                                        gg.ID,
                                        tipRenameData
                                    )
                                })
                            ),
                            CombRateMin: gg.Details[0].CombRateMin,
                            CombCountMin: gg.Details[0].CombCountMin,
                            System: gg.Details[0].CanBePlayedAsSystemBet,
                            StakeMax: gg.Details[0].StakeMax,
                            Info: gg.Info,

                            IsDetails: null
                        })
                    }
                    )
                })
            );
        };

        return {
            loadAsync,
            reloadAsync
        };
    });
